import { http } from '../lib/api/http';
import { getCurrentAuthUser } from '../firebaseAuth';
import { createNotification } from './notificationsApi';
import { mapReviewRecord } from './reviewsApi';

const RESERVATIONS_ENDPOINT = '/reservations';
const DEFAULT_LIMIT = 80;

export const RESERVATION_STATUS_OPTIONS = [
  { value: 'pending', label: 'Pendiente' },
  { value: 'accepted', label: 'Aceptada' },
  { value: 'confirmed', label: 'Confirmada' },
  { value: 'rejected', label: 'Rechazada' },
  { value: 'cancelled', label: 'Cancelada' },
];

export const RESERVATION_STATUS_LABELS = RESERVATION_STATUS_OPTIONS.reduce((acc, item) => {
  acc[item.value] = item.label;
  return acc;
}, {});

export const RESERVATION_FIELD_LABELS = {
  status: 'Estado',
  requestedFrom: 'Fecha de inicio',
  requestedTo: 'Fecha de termino',
  pickupLocation: 'Punto de recoleccion',
  dropoffLocation: 'Destino',
  cargoDescription: 'Descripcion de la mercancia',
  specialRequirements: 'Requerimientos especiales',
  contactName: 'Nombre de contacto',
  contactEmail: 'Correo de contacto',
  contactPhone: 'Telefono de contacto',
  notes: 'Notas',
  decisionNotes: 'Notas de decision',
  decisionAt: 'Fecha de decision',
  publication: 'Publicacion',
  unit: 'Unidad',
  company: 'Empresa',
  createdBy: 'Cliente',
};

const trimValue = value => (typeof value === 'string' ? value.trim() : value || '');

const mapCompany = record => {
  if (!record) {
    return {
      id: '',
      name: '',
      category: '',
      slug: '',
      phoneNumber: '',
      contactPhone: '',
      contactEmail: '',
      contactName: '',
      whatsapp: '',
      websiteUrl: '',
    };
  }
  const item = Array.isArray(record) ? record[0] : record;
  return {
    id: item?.id || '',
    name: item?.name || '',
    category: item?.category || '',
    slug: item?.slug || '',
    phoneNumber: item?.phoneNumber || item?.phone_number || item?.telefono || '',
    contactPhone: item?.contactPhone || item?.contact_phone || '',
    contactEmail: item?.contactEmail || item?.contact_email || item?.email || '',
    contactName: item?.contactName || item?.contact_name || item?.ownerName || '',
    whatsapp: item?.whatsapp || item?.whatsappPhone || item?.whatsapp_phone || '',
    websiteUrl: item?.websiteUrl || item?.website_url || item?.website || '',
    logoUrl: item?.logoUrl || item?.logo_url || item?.logo || '',
  };
};

const mapCustomer = record => {
  if (!record) {
    return {
      id: '',
      name: '',
      email: '',
      phone: '',
      companyId: '',
      companyName: '',
    };
  }
  const item = Array.isArray(record) ? record[0] : record;
  return {
    id: item?.id || '',
    name: item?.name || item?.displayName || item?.display_name || item?.username || item?.email || '',
    email: item?.email || '',
    phone: item?.phoneNumber || item?.phone_number || '',
    companyId: item?.company || item?.companyId || item?.company_id || '',
    companyName: item?.companyName || item?.company_name || '',
  };
};

const mapPublication = record => {
  if (!record) {
    return {
      id: '',
      title: '',
      status: '',
      unitId: '',
      unitName: '',
      companyId: '',
      type: '',
    };
  }
  const item = Array.isArray(record) ? record[0] : record;
  return {
    id: item?.id || '',
    title: item?.title || '',
    status: item?.status || '',
    unitId: item?.unitId || item?.unit_id || item?.unit || '',
    unitName: item?.unitName || item?.unit_name || '',
    companyId: item?.company || item?.companyId || item?.company_id || '',
    type: item?.type || item?.publication_type || '',
  };
};

const mapUnit = record => {
  if (!record) {
    return {
      id: '',
      model: '',
      tractorType: '',
      economicNumber: '',
      status: 'available',
    };
  }
  const item = Array.isArray(record) ? record[0] : record;
  return {
    id: item?.id || '',
    model: item?.model || item?.unit_model || '',
    tractorType: item?.tractorType || item?.tractor_type || '',
    economicNumber: item?.economicNumber || item?.economic_number || '',
    status: item?.status || 'available',
  };
};

const mapReviewExpand = record => {
  if (!record) return null;
  if (Array.isArray(record)) {
    return record.length ? mapReviewRecord(record[0]) : null;
  }
  return mapReviewRecord(record);
};

const mapReservationRecord = record => {
  if (!record) return null;
  const root = Array.isArray(record) ? record[0] : record;
  return {
    id: root.id,
    status: root.status || 'pending',
    serviceStatus: root.serviceStatus || root.service_status || 'unknown',
    serviceStatusConfirmedAt: root.serviceStatusConfirmedAt || root.service_status_confirmed_at || '',
    requestedFrom: root.requestedFrom || root.requested_from || '',
    requestedTo: root.requestedTo || root.requested_to || '',
    pickupLocation: root.pickupLocation || root.pickup_location || '',
    dropoffLocation: root.dropoffLocation || root.dropoff_location || '',
    cargoDescription: root.cargoDescription || root.cargo_description || '',
    specialRequirements: root.specialRequirements || root.special_requirements || '',
    contactName: root.contactName || root.contact_name || '',
    contactEmail: root.contactEmail || root.contact_email || '',
    contactPhone: root.contactPhone || root.contact_phone || '',
    notes: root.notes || '',
    decisionNotes: root.decisionNotes || root.decision_notes || '',
    decisionAt: root.decisionAt || root.decision_at || '',
    publicationId: root.publicationId || root.publication_id || root.publication || '',
    unitId: root.unitId || root.unit_id || root.unit || '',
    companyId: root.companyId || root.company_id || root.company || '',
    createdBy: root.createdBy || root.created_by || '',
    created: root.createdAt || root.created_at || root.created || '',
    updated: root.updatedAt || root.updated_at || root.updated || '',
    budget: root.budget || '',
    budgetLabel: root.budgetLabel || root.budget_label || '',
    budgetCurrency: root.budgetCurrency || root.budget_currency || '',
    budgetAmount: root.budgetAmount ?? root.budget_amount ?? null,
    quoteAmount:
      root.quoteAmount ??
      root.quote_amount ??
      root.quotedPrice ??
      root.quoted_price ??
      root.price ??
      null,
    quoteCurrency: root.quoteCurrency || root.quote_currency || root.currency || '',
    totalCost: root.totalCost ?? root.total_cost ?? null,
    currency: root.currency || '',
    publication: mapPublication(root.publication),
    unit: mapUnit(root.unit),
    company: mapCompany(root.company),
    customer: mapCustomer(root.customer || root.createdBy || root.created_by),
    clientReviewId: root.clientReviewId || root.client_review_id || root.clientReview || '',
    providerReviewId:
      root.providerReviewId || root.provider_review_id || root.providerReview || '',
    clientReview: mapReviewExpand(root.clientReview || root.client_review),
    providerReview: mapReviewExpand(root.providerReview || root.provider_review),
  };
};

const buildPayload = (payload = {}, { withDefaults = false } = {}) => {
  const body = {};

  const assignRaw = (field, value) => {
    if (value !== undefined) {
      body[field] = value;
    }
  };

  const assignTrimmed = (field, value) => {
    if (value !== undefined) {
      body[field] = trimValue(value);
    }
  };

  assignRaw('status', payload.status);
  assignRaw('requested_from', payload.requestedFrom ?? payload.requested_from);
  assignRaw('requested_to', payload.requestedTo ?? payload.requested_to);
  assignTrimmed('pickup_location', payload.pickupLocation ?? payload.pickup_location);
  assignTrimmed('dropoff_location', payload.dropoffLocation ?? payload.dropoff_location);
  assignTrimmed('cargo_description', payload.cargoDescription ?? payload.cargo_description);
  assignTrimmed(
    'special_requirements',
    payload.specialRequirements ?? payload.special_requirements
  );
  assignTrimmed('contact_name', payload.contactName ?? payload.contact_name);
  assignTrimmed('contact_email', payload.contactEmail ?? payload.contact_email);
  assignTrimmed('contact_phone', payload.contactPhone ?? payload.contact_phone);
  assignTrimmed('notes', payload.notes);
  assignTrimmed('decision_notes', payload.decisionNotes ?? payload.decision_notes);
  assignRaw('decision_at', payload.decisionAt ?? payload.decision_at);
  assignRaw('service_status', payload.serviceStatus ?? payload.service_status);
  assignRaw(
    'service_status_confirmed_at',
    payload.serviceStatusConfirmedAt ?? payload.service_status_confirmed_at
  );

  if (payload.publicationId !== undefined || payload.publication !== undefined) {
    body.publication_id = payload.publicationId || payload.publication;
  }

  if (payload.unitId !== undefined || payload.unit !== undefined) {
    body.unit_id = payload.unitId || payload.unit;
  }

  if (payload.companyId !== undefined || payload.company !== undefined) {
    body.company_id = payload.companyId || payload.company;
  }

  if (payload.createdBy !== undefined || payload.created_by !== undefined) {
    body.created_by = payload.createdBy ?? payload.created_by;
  }

  if (payload.clientReviewId !== undefined || payload.clientReview !== undefined) {
    body.client_review_id = payload.clientReviewId || payload.clientReview;
  }

  if (payload.providerReviewId !== undefined || payload.providerReview !== undefined) {
    body.provider_review_id = payload.providerReviewId || payload.providerReview;
  }

  if (withDefaults) {
    if (body.status === undefined) {
      body.status = 'pending';
    }
    if (body.serviceStatus === undefined) {
      body.serviceStatus = 'unknown';
    }
  }

  return body;
};

const parseApiProblem = (error = {}) => {
  const response = error?.response?.data || {};
  const status = error?.response?.status ?? response?.status ?? null;
  const rootMessage =
    (typeof response?.detail === 'string' && response.detail.trim()) ||
    (typeof response?.message === 'string' && response.message.trim()) ||
    (typeof error?.message === 'string' && error.message.trim()) ||
    'No pudimos procesar tu solicitud.';

  return {
    status,
    message: rootMessage,
    fieldErrors: [],
  };
};

const buildDetailedErrorMessage = ({ message, fieldErrors, status }) => {
  const normalizedMessage =
    message && message !== 'Failed to create record.'
      ? message
      : status === 0
        ? 'No pudimos contactar al servidor. Intenta nuevamente en unos minutos.'
        : 'No pudimos registrar tu reserva.';

  if (!Array.isArray(fieldErrors) || !fieldErrors.length) {
    return normalizedMessage;
  }

  const detailsText = fieldErrors
    .map(detail => `${detail.label}: ${detail.message}`)
    .join(' ');
  return `${normalizedMessage} Verifica los siguientes campos: ${detailsText}`;
};

export async function createReservation(payload) {
  const body = buildPayload(payload, { withDefaults: true });
  try {
    const { data } = await http.post(RESERVATIONS_ENDPOINT, body);
    const mapped = mapReservationRecord(data);

    if (mapped.companyId) {
      const customerName =
        mapped.customer?.name ||
        mapped.customer?.email ||
        mapped.contactName ||
        'Un cliente';
      const publicationTitle =
        mapped.publication?.title ||
        mapped.unit?.model ||
        mapped.unit?.tractorType ||
        'una de tus publicaciones';
      const destinationUrl = `/dashboard?section=reservas${
        mapped.id ? `&reservation=${mapped.id}` : ''
      }`;

      createNotification({
        companyId: mapped.companyId,
        type: 'reservation',
        title: 'Nueva solicitud de reserva',
        message: `${customerName} quiere reservar ${publicationTitle}.`,
        reservationId: mapped.id,
        destinationUrl,
        createdBy: mapped.createdBy || mapped.customer?.id || '',
      }).catch(error => {
        console.warn('No se pudo registrar la notificación de reserva.', error);
      });
    }

    return mapped;
  } catch (error) {
    const parsedError = parseApiProblem(error);
    const detailedMessage = buildDetailedErrorMessage(parsedError);
    const enrichedError = new Error(detailedMessage);
    enrichedError.status = parsedError.status ?? error?.status ?? null;
    enrichedError.fieldErrors = parsedError.fieldErrors || [];
    enrichedError.cause = error;
    throw enrichedError;
  }
}

export async function updateReservation(id, payload) {
  if (!id) {
    throw new Error('El identificador de la reserva es requerido.');
  }
  const body = buildPayload(payload);
  try {
    const { data } = await http.patch(`${RESERVATIONS_ENDPOINT}/${encodeURIComponent(id)}`, body);
    return mapReservationRecord(data);
  } catch (error) {
    const parsedError = parseApiProblem(error);
    const enrichedError = new Error(parsedError.message);
    enrichedError.status = parsedError.status ?? error?.status ?? null;
    enrichedError.fieldErrors = parsedError.fieldErrors || [];
    enrichedError.cause = error;
    throw enrichedError;
  }
}

export async function setReservationStatus(id, status, { decisionNotes = '', decisionAt = '' } = {}) {
  if (!id) {
    throw new Error('El identificador de la reserva es requerido.');
  }
  const body = {
    status,
    decision_notes: trimValue(decisionNotes),
    decision_at: decisionAt || new Date().toISOString(),
  };
  try {
    const { data } = await http.patch(`${RESERVATIONS_ENDPOINT}/${encodeURIComponent(id)}`, body);
    const mapped = mapReservationRecord(data);

    if (status === 'accepted' || status === 'rejected') {
      const customerCompanyId = mapped.customer?.companyId || '';
      if (customerCompanyId) {
        const providerName = mapped.company?.name || 'el proveedor';
        const publicationTitle =
          mapped.publication?.title ||
          mapped.unit?.model ||
          mapped.unit?.tractorType ||
          'tu solicitud';
        const destinationUrl = `/dashboard?section=reservas-cliente${
          mapped.id ? `&reservation=${mapped.id}` : ''
        }`;
      const actorId = getCurrentAuthUser()?.id || '';
        const isAccepted = status === 'accepted';
        const actionVerb = isAccepted ? 'aceptó' : 'rechazó';
        const notificationTitle = isAccepted ? 'Tu reserva fue aceptada' : 'Tu reserva fue rechazada';

        createNotification({
          companyId: customerCompanyId,
          type: 'reservation',
          title: notificationTitle,
          message: `${providerName} ${actionVerb} tu reserva para ${publicationTitle}.`,
          reservationId: mapped.id,
          destinationUrl,
          createdBy: actorId,
          data: {
            reservationStatus: status,
          },
        }).catch(error => {
          const statusLabel = isAccepted ? 'aceptada' : 'rechazada';
          console.warn(`No se pudo notificar al cliente sobre la reserva ${statusLabel}.`, error);
        });
      } else {
        const statusLabel = status === 'accepted' ? 'aceptada' : 'rechazada';
        console.warn(
          `No se pudo enviar la notificación de reserva ${statusLabel}: cliente sin empresa asociada.`
        );
      }
    }

    if (status === 'confirmed') {
      notifyClientAboutServiceSurvey(mapped);
    }

    return mapped;
  } catch (error) {
    const parsedError = parseApiProblem(error);
    throw new Error(parsedError.message || 'No se pudo actualizar la reserva.');
  }
}

const notifyClientAboutServiceSurvey = reservation => {
  if (!reservation) return;
  const customerCompanyId = reservation.customer?.companyId || '';
  if (!customerCompanyId) {
    console.warn(
      'No se pudo enviar la notificación de confirmación de servicio: el cliente no tiene empresa asociada.'
    );
    return;
  }
  const providerName = reservation.company?.name || 'el proveedor';
  const destinationUrl = `/dashboard?section=reservas-cliente${
    reservation.id ? `&reservation=${reservation.id}` : ''
  }`;
  createNotification({
    companyId: customerCompanyId,
    type: 'review',
    title: 'Confirma tu servicio',
    message: `¿Se realizó el servicio con ${providerName}?`,
    reservationId: reservation.id,
    destinationUrl,
    data: {
      surveyType: 'service_confirmation',
    },
  }).catch(error => {
    console.warn('No se pudo notificar al cliente para confirmar el servicio.', error);
  });
};

const notifyParticipantsAboutReviews = reservation => {
  if (!reservation) return;
  const providerCompanyId = reservation.companyId || '';
  const customerCompanyId = reservation.customer?.companyId || '';
  const providerName = reservation.company?.name || 'tu proveedor';
  const customerName = reservation.customer?.name || 'tu cliente';
  const reservationSuffix = reservation.id ? `&reservation=${reservation.id}` : '';

  if (customerCompanyId) {
    const destinationUrl = `/dashboard?section=reservas-cliente${reservationSuffix}`;
    createNotification({
      companyId: customerCompanyId,
      type: 'review',
      title: 'Califica a tu proveedor',
      message: `Comparte cómo te fue con ${providerName}.`,
      reservationId: reservation.id,
      destinationUrl,
      data: {
        reviewDirection: 'client_to_provider',
      },
    }).catch(error => {
      console.warn('No se pudo notificar al cliente para calificar al proveedor.', error);
    });
  } else {
    console.warn(
      'No se pudo enviar la notificación de reseña al cliente: empresa no encontrada.'
    );
  }

  if (providerCompanyId) {
    const destinationUrl = `/dashboard?section=reservas${reservationSuffix}`;
    createNotification({
      companyId: providerCompanyId,
      type: 'review',
      title: 'Califica a tu cliente',
      message: `Evalúa la experiencia con ${customerName}.`,
      reservationId: reservation.id,
      destinationUrl,
      data: {
        reviewDirection: 'provider_to_client',
      },
    }).catch(error => {
      console.warn('No se pudo notificar al proveedor para calificar al cliente.', error);
    });
  } else {
    console.warn(
      'No se pudo enviar la notificación de reseña al proveedor: empresa no encontrada.'
    );
  }
};

export async function setReservationServiceStatus(id, completed = false) {
  if (!id) {
    throw new Error('El identificador de la reserva es requerido.');
  }
  const nowIso = new Date().toISOString();
  const body = {
    service_status: completed ? 'completed' : 'not_completed',
    service_status_confirmed_at: nowIso,
  };
  try {
    const { data } = await http.patch(`${RESERVATIONS_ENDPOINT}/${encodeURIComponent(id)}`, body);
    const mapped = mapReservationRecord(data);
    if (completed) {
      notifyParticipantsAboutReviews(mapped);
    }
    return mapped;
  } catch (error) {
    const parsedError = parseApiProblem(error);
    throw new Error(parsedError.message || 'No se pudo registrar la confirmación de servicio.');
  }
}

export async function getReservationById(id) {
  if (!id) {
    throw new Error('El identificador de la reserva es requerido.');
  }
  const { data } = await http.get(`${RESERVATIONS_ENDPOINT}/${encodeURIComponent(id)}`);
  return mapReservationRecord(data);
}

export async function listReservations({
  companyId,
  publicationId,
  unitId,
  createdBy,
  contactEmail,
  contactPhone,
  status,
  limit = DEFAULT_LIMIT,
} = {}) {
  const params = { limit };
  if (companyId) {
    params.company_id = companyId;
  }
  if (publicationId) {
    params.publication_id = publicationId;
  }
  if (unitId) {
    params.unit_id = unitId;
  }
  if (createdBy) {
    params.created_by = createdBy;
  }
  if (contactEmail) {
    params.contact_email = contactEmail;
  }
  if (contactPhone) {
    params.contact_phone = contactPhone;
  }
  if (Array.isArray(status) && status.length) {
    params.status = status;
  } else if (status) {
    params.status = [status];
  }

  const { data } = await http.get(RESERVATIONS_ENDPOINT, { params });
  const items = Array.isArray(data?.items) ? data.items : data;
  return (Array.isArray(items) ? items : []).map(mapReservationRecord);
}
